import styled from '@emotion/styled';
import {
  Box,
  MenuItem,
  Select,
  Stack,
  Typography,
  CircularProgress,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import GenButton from '../GenButton';
import AddCircleIcon from '../Icons/AddIcon';

const VehicleDetails = ({
  year,
  dealership,
  photoCount,
  totalAmount,
  onChangeSelect,
  selectValue,
  onClickAddEstimates,
  variant = 'primary',
  isAddEstimateDisabled,
  showAsMiscOrder,
  apiLoading = false,
  orderId,
  showPhotoCount = false,
}) => {
  const PREFIX = 'VehicleDetails';
  const { t } = useTranslation('global');

  const classes = {
    image: `${PREFIX}-image`,
    infoRow: `${PREFIX}-infoRow`,
    infoRowRo: `${PREFIX}-infoRowRo`,
    infoRowTotal: `${PREFIX}-infoRowTotal`,
    infoColumn: `${PREFIX}-infoColumn`,
    totalAmountColumn: `${PREFIX}-totalAmountColumn`,
    selectColumn: `${PREFIX}-selectColumn`,
    baseRow: `${PREFIX}-baseRow`,
    selectDropDown: `${PREFIX}-selectDropDown`,
    yearRoColumn: `${PREFIX}-yearRoColumn`,
  };

  const Container = styled('div')(({ theme }) => ({
    padding: '16px',
    justifyContent: 'space-between',
    flexDirection: 'row',
    width: '100%',
    display: 'flex',
    [theme.breakpoints.up('lg')]: {
      maxHeight: '130px',
    },
    backgroundColor: variant === 'primary' ? 'white' : '#DDE8FF',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
    [`& .${classes.infoRow}`]: {
      display: 'flex',
      gap: 4,
      flexDirection: 'column',
      alignItems: 'baseline',
      maxWidth: '33%',

      [theme.breakpoints.down('sm')]: {
        flexDirection: 'row',
        maxWidth: '100%',
      },
    },
    [`& .${classes.yearRoColumn}`]: {
      display: 'flex',
      gap: '15px',
    },
    [`& .${classes.baseRow}`]: {
      display: 'flex',
      flex: 1,
      gap: '20px',
      alignItems: 'center',
      marginBottom: '20px',
      [theme.breakpoints.down('md')]: {
        justifyContent: 'space-around',
      },
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '8px',
      },
    },
    [`& .${classes.infoRowRo}`]: {
      [theme.breakpoints.up('sm')]: {
        display: 'flex',
      },
      gap: 4,
      flexDirection: 'row',
      alignItems: 'center',
    },
    [`& .${classes.infoRowTotal}`]: {
      [theme.breakpoints.up('sm')]: {
        display: 'none',
      },
      [theme.breakpoints.down('sm')]: {
        display: 'flex',
      },
      gap: 4,
      flexDirection: 'row',
      alignItems: 'center',
    },
    [`& .${classes.infoColumn}`]: {
      display: 'flex',
      gap: 8,
      flexDirection: 'column',
      [theme.breakpoints.up('md')]: {
        marginLeft: 'auto',
      },
    },
    [`& .${classes.totalAmountColumn}`]: {
      display: 'flex',
      gap: '15px',
      [theme.breakpoints.up('sm')]: {
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
      },
      [theme.breakpoints.up('md')]: {
        marginLeft: 'auto',
      },
    },
    [`& .${classes.selectDropDown}`]: {
      backgroundColor: '#FFF',
    },
    [`& .${classes.selectColumn}`]: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      alignItems: 'flex-end',
      marginLeft: '105px',
      [theme.breakpoints.down('md')]: {
        flexDirection: 'row',
        justifyContent: 'center',
        gap: 8,
        alignItems: 'center',
        marginLeft: '0px',
      },
    },
  }));

  const dropDownData = [
    {
      label: 'All',
      value: 1,
    },
    {
      label: 'Estimates',
      value: 2,
    },
    {
      label: 'Packages',
      value: 3,
    },
    {
      label: 'Service Order',
      value: 4,
    },
    {
      label: 'Simple Work Order',
      value: 5,
    },
  ];

  const InfoTitle = text => {
    return (
      <Typography fontSize={'14px'} fontWeight={'600'} color="text.primary">
        {text.text}:
      </Typography>
    );
  };

  const InfoSubtitle = subtitle => (
    <Typography fontSize={'16px'} color="text.secondary">
      {subtitle.subtitle}
    </Typography>
  );
  return (
    <Container>
      <Box className={classes.baseRow}>
        <Box className={classes.infoRow}>
          <InfoTitle text={t('dealership')} />
          <InfoSubtitle subtitle={dealership} />
        </Box>
        <Box className={classes.infoColumn}>
          <Stack
            spacing={1}
            sx={{
              p: 2,
              maxWidth: 300,
            }}
          >
            <Box sx={{ display: 'flex' }}>
              <Box sx={{ width: photoCount ? '100px' : '40px' }}>
                <Typography variant="body2" fontWeight="bold">
                  Year:
                </Typography>
              </Box>
              <Typography variant="body2">{year}</Typography>
            </Box>
            {(apiLoading || photoCount?.[orderId] !== null) && showPhotoCount && (
              <Box sx={{ display: 'flex' }}>
                <Box sx={{ width: '100px' }}>
                  <Typography variant="body2" fontWeight="bold">
                    Photo Count:
                  </Typography>
                </Box>
                <Typography variant="body2">
                  {apiLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    photoCount?.[orderId]
                  )}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>

        <Box className={classes.totalAmountColumn}>
          <Typography
            fontWeight={'600'}
            variant="subtitle1"
            color="text.primary"
            fontSize={14}
          >
            {t('totalAmount')}:
          </Typography>
          <Typography
            fontSize={18}
            color="text.secondary"
            fontWeight={'600'}
            data-testid="total-amount"
          >
            ${totalAmount}
          </Typography>
        </Box>
      </Box>

      <Box className={classes.selectColumn}>
        {!isAddEstimateDisabled ? (
          <GenButton
            color="primary"
            variant="outlined"
            startIcon={<AddCircleIcon />}
            text={
              showAsMiscOrder
                ? t('vehicleDetailsBase.addMiscOrders')
                : t('vehicleDetailsBase.addEstimates')
            }
            size="medium"
            onClick={onClickAddEstimates}
            data-testid={`vehiceDetails-add-estimate`}
          />
        ) : null}
        <Select
          value={selectValue}
          className={classes.selectDropDown}
          onChange={evt => onChangeSelect(evt.target.value)}
          sx={{ height: 40, width: 249 }}
          data-testid={'select-dropdown'}
        >
          {dropDownData.map((selectType, index) => (
            <MenuItem
              key={`menuItem-${index + 1}`}
              data-testid={`select-menuItem-${index + 1}`}
              value={selectType.value}
            >
              {selectType.label}
            </MenuItem>
          ))}
        </Select>
      </Box>
    </Container>
  );
};

export default VehicleDetails;
